import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  Check,
  Loader2,
  Menu,
  Mic,
  Play,
  Plus,
  Send,
  Sparkles,
} from "lucide-react";
import { clsx } from "clsx";
import { isLeaf } from "../lib/goalCore";
import { inboxTasks, mergeSuggestions, ownerLabel } from "../lib/inboxCore";
import { todayIso } from "../lib/laneCore";
import { overdueGoals } from "../lib/triageCore";
import { DelegateProvider, delegationDefaultProvider } from "../lib/delegationCore";
import {
  AgentPreset,
  api,
  CLAUDE_EFFORTS,
  CODEX_EFFORTS,
  isTauriEnvironment,
  type Goal,
  type InboxSuggestion,
  type Task,
} from "../lib/tauri";
import { PresetPicker } from "./command/PresetPicker";
import { useGoalStore } from "../stores/goalStore";
import { useProjectStore } from "../stores/projectStore";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { GlobalSearchButton } from "./GlobalSearch";

const OWNER_OPTIONS = ["", "taiga", "codex", "claude", "grok", "hermes"];

export function InboxView({ onMenuClick }: { onMenuClick?: () => void }) {
  const allTasks = useTaskStore((s) => s.allTasks);
  const createTask = useTaskStore((s) => s.createTask);
  const goals = useGoalStore((s) => s.goals);
  const loadGoals = useGoalStore((s) => s.load);
  const projects = useProjectStore((s) => s.projects);
  const setVoiceOpen = useUiStore((s) => s.setVoiceOpen);
  const showToast = useUiStore((s) => s.showToast);
  const [text, setText] = useState("");
  const [classifying, setClassifying] = useState(false);
  const [suggestions, setSuggestions] = useState<Record<string, InboxSuggestion>>({});

  useEffect(() => {
    void loadGoals();
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://goals-changed", () => {
        void loadGoals();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [loadGoals]);

  const knownIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);
  const tasks = useMemo(() => inboxTasks(allTasks, knownIds), [allTasks, knownIds]);
  const rows = useMemo(
    () => mergeSuggestions(tasks, Object.values(suggestions)),
    [tasks, suggestions]
  );
  const today = todayIso();
  const overdue = useMemo(() => overdueGoals(goals, today), [goals, today]);

  const quickAdd = async () => {
    if (!text.trim()) return;
    await createTask({ title: text.trim() }); // project なし → インボックスに入る
    setText("");
  };

  const classify = async () => {
    if (tasks.length === 0) return;
    setClassifying(true);
    try {
      const briefs = projects
        .filter((p) => !p.archived)
        .map((p) => ({ id: p.id, name: p.name, kind: p.kind }));
      const result = (await api.aiClassifyInbox?.(tasks, briefs)) ?? [];
      setSuggestions(Object.fromEntries(result.map((s) => [s.taskId, s])));
      if (result.length === 0) showToast("AI提案なし。手動で分類してください");
    } catch (e) {
      // 設計判断2: 手動 select は常設なので分類作業は止まらない
      const message = e instanceof Error ? e.message : String(e);
      showToast(
        message.includes("Flow api method not found")
          ? "この機能はデスクトップ版のみです"
          : "ローカルAI(Ollama)に接続できません。手動で分類するか、ollama serve 起動後に再実行してください"
      );
    } finally {
      setClassifying(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3 dark:border-border-dark">
        <button
          type="button"
          aria-label="メニュー"
          onClick={onMenuClick}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h2 className="text-base font-bold">インボックス</h2>
        <span className="font-mono text-xs text-muted dark:text-muted-dark">{tasks.length}</span>
        <GlobalSearchButton className="ml-auto" />
        <button
          type="button"
          onClick={() => void classify()}
          disabled={classifying || tasks.length === 0}
          className="flex items-center gap-1.5 rounded-md bg-gradient-to-r from-brand-500 to-info-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        >
          {classifying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          AIで分類
        </button>
      </header>

      {/* 即投入: テキスト + 音声（既存 VoiceCaptureSheet 再利用） */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-3 dark:border-border-dark">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) void quickAdd();
          }}
          placeholder="依頼・思いつきをそのまま放り込む（Enterで追加）"
          className="h-10 flex-1 rounded-md border border-border bg-bg px-3 text-sm dark:border-border-dark dark:bg-bg-dark"
        />
        <button
          type="button"
          aria-label="追加"
          onClick={() => void quickAdd()}
          disabled={!text.trim()}
          className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-500 text-white disabled:opacity-50"
        >
          <Plus className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="音声で追加"
          onClick={() => setVoiceOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted hover:bg-hover dark:border-border-dark dark:text-muted-dark dark:hover:bg-hover-dark"
        >
          <Mic className="h-5 w-5" />
        </button>
      </div>

      <div className="flow-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {overdue.length > 0 && (
          <OverdueTriageSection overdue={overdue} allGoals={goals} allTasks={allTasks} today={today} />
        )}
        {rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted dark:text-muted-dark">
            インボックスは空です。音声・テキストで放り込むとここに溜まります。
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map(({ task, suggestion }) => (
              <InboxRow key={task.id} task={task} suggestion={suggestion} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function addDaysIso(baseIso: string, days: number): string {
  const [year, month, day] = baseIso.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return todayIso(date);
}

function OverdueTriageSection({
  overdue,
  allGoals,
  allTasks,
  today,
}: {
  overdue: Goal[];
  allGoals: Goal[];
  allTasks: Task[];
  today: string;
}) {
  const goal = overdue[0];
  const remaining = Math.max(0, overdue.length - 1);
  const updateGoal = useGoalStore((s) => s.update);
  const createTask = useTaskStore((s) => s.createTask);
  const showToast = useUiStore((s) => s.showToast);
  const [processing, setProcessing] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const leaf = isLeaf(allGoals, goal.id);

  useEffect(() => {
    setCustomDate("");
  }, [goal.id]);

  const updateDue = async (dueDate: string, label: string) => {
    setProcessing(true);
    try {
      await updateGoal(goal.id, { dueDate });
      showToast(label);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "ゴールの更新に失敗しました");
    } finally {
      setProcessing(false);
    }
  };

  const doToday = async () => {
    setProcessing(true);
    try {
      if (leaf) {
        const hasTodayTask = allTasks.some(
          (task) =>
            !task.deleted &&
            !task.completed &&
            task.goalId === goal.id &&
            task.dueDate === today
        );
        if (!hasTodayTask) {
          await createTask({
            title: goal.title,
            description: goal.dod || goal.description || undefined,
            dueDate: today,
            goalId: goal.id,
          });
        }
      }
      await updateGoal(goal.id, { dueDate: today });
      showToast(leaf ? "今日のタスクに送りました" : "期日を今日に更新しました");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "今日への送信に失敗しました");
    } finally {
      setProcessing(false);
    }
  };

  const dormancy = async () => {
    setProcessing(true);
    try {
      await updateGoal(goal.id, { status: "dormant" });
      showToast("休眠にしました");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "休眠への変更に失敗しました");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <section className="mb-3 border-b border-border pb-3 dark:border-border-dark">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs font-bold text-danger-500">
          <CalendarClock className="h-4 w-4" />
          期日超過
        </div>
        <span className="rounded-full bg-danger-500/10 px-2 py-0.5 text-[10px] font-semibold text-danger-500">
          残り{remaining}件
        </span>
      </div>
      <div className="rounded-lg border border-danger-500/30 bg-danger-500/5 p-3 dark:border-danger-500/40">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{goal.title}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-danger-500">
              <span className="rounded-full bg-danger-500/10 px-2 py-0.5 font-semibold">
                {goal.dueDate}
              </span>
              <span className="rounded-full bg-bg px-2 py-0.5 text-muted dark:bg-bg-dark dark:text-muted-dark">
                {leaf ? "葉ゴール" : "親ゴール"}
              </span>
            </div>
          </div>
          {processing && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-danger-500" />}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => void doToday()}
            disabled={processing}
            className="inline-flex items-center gap-1 rounded-md bg-brand-500 px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
          >
            <Send className="h-3 w-3" />
            今日やる
          </button>
          <button
            type="button"
            onClick={() => void updateDue(addDaysIso(today, 1), "期日を明日にずらしました")}
            disabled={processing}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-bg px-2.5 py-1 text-[11px] font-semibold text-muted hover:bg-hover disabled:opacity-50 dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark dark:hover:bg-hover-dark"
          >
            <CalendarPlus className="h-3 w-3" />
            +1日
          </button>
          <button
            type="button"
            onClick={() => void updateDue(addDaysIso(today, 7), "期日を1週ずらしました")}
            disabled={processing}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-bg px-2.5 py-1 text-[11px] font-semibold text-muted hover:bg-hover disabled:opacity-50 dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark dark:hover:bg-hover-dark"
          >
            <CalendarPlus className="h-3 w-3" />
            +1週
          </button>
          <label className="inline-flex items-center gap-1 rounded-md border border-border bg-bg px-2 py-1 text-[11px] text-muted dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark">
            <CalendarPlus className="h-3 w-3" />
            <input
              type="date"
              min={today}
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              disabled={processing}
              className="w-[7.5rem] bg-transparent text-[11px] outline-none"
            />
          </label>
          <button
            type="button"
            onClick={() => void updateDue(customDate, "期日を更新しました")}
            disabled={processing || !customDate || customDate < today}
            className="rounded-md border border-border bg-bg px-2.5 py-1 text-[11px] font-semibold text-muted hover:bg-hover disabled:opacity-50 dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark dark:hover:bg-hover-dark"
          >
            指定
          </button>
          <button
            type="button"
            onClick={() => void dormancy()}
            disabled={processing}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-bg px-2.5 py-1 text-[11px] font-semibold text-muted hover:bg-hover disabled:opacity-50 dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark dark:hover:bg-hover-dark"
          >
            <Archive className="h-3 w-3" />
            休眠へ
          </button>
        </div>
      </div>
    </section>
  );
}

function InboxRow({ task, suggestion }: { task: Task; suggestion: InboxSuggestion | null }) {
  const updateTask = useTaskStore((s) => s.updateTask);
  const projects = useProjectStore((s) => s.projects);
  const setEditorOpen = useUiStore((s) => s.setEditorOpen);
  const showToast = useUiStore((s) => s.showToast);

  const suggestedProject = suggestion?.projectId
    ? projects.find((p) => p.id === suggestion.projectId) ?? null
    : null;

  const apply = async (patch: Record<string, unknown>, label: string) => {
    try {
      await updateTask(task.id, patch);
      showToast(label);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "更新に失敗しました");
    }
  };

  const setAppView = useUiStore((s) => s.setAppView);
  const [delegating, setDelegating] = useState<DelegateProvider | null>(null);
  const [preset, setPreset] = useState<AgentPreset>("normal");
  const [modelOverride, setModelOverride] = useState("");
  const [effortOverride, setEffortOverride] = useState("");
  // AI提案 owner（無ければ確定済み owner）が codex/claude のときだけデフォルト強調。選択は常に自由
  const defaultProvider = delegationDefaultProvider(suggestion, task.owner);

  const delegate = async (provider: DelegateProvider) => {
    if (delegating) return; // 二重投入防止（in-flight 中 disabled と二重ガード）
    // 押したボタンの provider と override の整合を送信前に保証する。
    // PresetPicker の表示 provider は defaultProvider 固定のため、codex/claude 両ボタン常設の
    // この画面では不一致 override が起き得る。不一致分はここでクリアし、バックエンド400を防ぐ。
    const validEfforts: readonly string[] = provider === "claude" ? CLAUDE_EFFORTS : CODEX_EFFORTS;
    const safeModel = provider === "claude" ? modelOverride : "";
    const safeEffort = validEfforts.includes(effortOverride) ? effortOverride : "";
    if (safeModel !== modelOverride) setModelOverride(safeModel);
    if (safeEffort !== effortOverride) setEffortOverride(safeEffort);
    setDelegating(provider);
    let taskUpdated = false;
    try {
      // (a) project/owner を確定保存（既存 update_task 経路）
      await updateTask(task.id, {
        owner: provider,
        ...(suggestion?.projectId ? { projectId: suggestion.projectId } : {}),
      });
      taskUpdated = true;
      // (b) 委任プロンプトは Rust 側（service::delegation）で合成して enqueue
      await api.delegateTaskJob?.({
        taskId: task.id,
        provider,
        preset: preset !== "normal" ? preset : null,
        modelOverride: safeModel || null,
        effortOverride: safeEffort || null,
      });
      // (c) 稼働ボードへ誘導
      showToast(`${ownerLabel(provider)} に委任しました。稼働ボードで進行を確認できます`);
      setAppView("command");
    } catch (e) {
      // 注意: api は Proxy なので ?. では短絡しない。メソッド未実装は "Flow api method not found" で投げる
      const message = e instanceof Error ? e.message : String(e);
      if (taskUpdated) {
        // (a) 成功 + (b) 失敗: タスクは案件レーンに移動済み。
        // 委任ジョブ自体は投入されていないため「再実行できます」とは言わず、
        // バックエンドのエラー文言をそのまま見せる。
        showToast(
          message.includes("Flow api method not found")
            ? "案件レーンに移動済み。委任はデスクトップ版のみ対応です"
            : `案件レーンに移動済み。委任の投入に失敗: ${message}`
        );
      } else {
        showToast(
          message.includes("Flow api method not found")
            ? "ワンタップ委任はデスクトップ版のみ対応です"
            : message
        );
      }
    } finally {
      setDelegating(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3 dark:border-border-dark dark:bg-card-dark">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => setEditorOpen(true, task.id)}
          className="flex-1 text-left text-sm font-medium leading-snug"
        >
          {task.title}
        </button>
        {task.owner && (
          <span className="rounded-full bg-bg px-2 py-0.5 text-[10px] text-muted dark:bg-bg-dark dark:text-muted-dark">
            委任: {ownerLabel(task.owner)}
          </span>
        )}
      </div>

      {/* AI 提案チップ（ワンタップ確定） */}
      {suggestion && (suggestedProject || suggestion.owner) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {suggestedProject && (
            <button
              type="button"
              onClick={() =>
                void apply(
                  { projectId: suggestedProject.id },
                  `「${suggestedProject.name}」へ分類しました`
                )
              }
              className="flex items-center gap-1 rounded-full border border-brand-500/40 bg-brand-500/10 px-2.5 py-1 text-[11px] font-medium text-brand-700 dark:text-brand-200"
            >
              <ArrowRight className="h-3 w-3" />
              {suggestedProject.name}
            </button>
          )}
          <span className="rounded-full bg-bg px-2.5 py-1 text-[11px] text-muted dark:bg-bg-dark dark:text-muted-dark">
            委任候補: {ownerLabel(suggestion.owner)}
          </span>
          <button
            type="button"
            onClick={() =>
              void apply(
                {
                  ...(suggestedProject ? { projectId: suggestedProject.id } : {}),
                  owner: suggestion.owner,
                },
                suggestedProject
                  ? `「${suggestedProject.name}」+ 委任 ${ownerLabel(suggestion.owner)} で確定`
                  : `委任先を ${ownerLabel(suggestion.owner)} に記録しました`
              )
            }
            className="flex items-center gap-1 rounded-full bg-brand-500 px-2.5 py-1 text-[11px] font-semibold text-white"
          >
            <Check className="h-3 w-3" /> まとめて確定
          </button>
          <span className="text-[10px] text-dim">
            確信度 {Math.round(suggestion.confidence * 100)}%
          </span>
        </div>
      )}

      {/* Phase 4: ワンタップ委任（常設）。taiga/grok/hermes 提案は従来どおり記録のみで実行経路なし */}
      <div className="mt-2">
        <PresetPicker
          provider={defaultProvider ?? "claude"}
          preset={preset}
          modelOverride={modelOverride}
          effortOverride={effortOverride}
          onPresetChange={setPreset}
          onModelOverrideChange={setModelOverride}
          onEffortOverrideChange={setEffortOverride}
        />
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        {(["codex", "claude"] as const).map((provider) => {
          const isDefault = defaultProvider === provider;
          return (
            <button
              key={provider}
              type="button"
              disabled={delegating !== null}
              onClick={() => void delegate(provider)}
              className={clsx(
                "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50",
                isDefault
                  ? "border-brand-500 bg-brand-500 text-white ring-1 ring-brand-500/50"
                  : "border-border bg-bg text-muted hover:bg-hover dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark dark:hover:bg-hover-dark"
              )}
            >
              {delegating === provider ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Play className="h-3 w-3" />
              )}
              {provider === "codex" ? "codexで実行" : "claudeで実行"}
              {isDefault && <span className="text-[9px] opacity-90">AI案</span>}
            </button>
          );
        })}
      </div>

      {/* 手動分類（常設 = Ollama 停止時のフォールバック） */}
      <div className="mt-2 flex items-center gap-2">
        <select
          aria-label="プロジェクトへ分類"
          value=""
          onChange={(e) => {
            if (e.target.value) {
              const p = projects.find((x) => x.id === e.target.value);
              void apply({ projectId: e.target.value }, `「${p?.name ?? ""}」へ分類しました`);
            }
          }}
          className="rounded border border-border bg-bg px-1.5 py-1 text-[11px] text-muted dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark"
        >
          <option value="">プロジェクトへ…</option>
          {projects
            .filter((p) => !p.archived)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
        <select
          aria-label="委任先"
          value={task.owner}
          onChange={(e) => void apply({ owner: e.target.value }, "委任先を記録しました")}
          className="rounded border border-border bg-bg px-1.5 py-1 text-[11px] text-muted dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark"
        >
          {OWNER_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o === "" ? "委任先…" : ownerLabel(o)}
            </option>
          ))}
        </select>
        <span className="ml-auto text-[10px] text-dim">{task.createdAt.slice(0, 10)}</span>
      </div>
    </div>
  );
}
